// Adapter over the HOST app's session (migration D4).
//
// The standalone BOLON backoffice kept the JWT it obtained from the dev gateway
// login in sessionStorage under its own key. Inside fe-essilor-dashboard the
// token is whatever the host login stored — the Zustand `persist` store named
// "user", i.e. localStorage "user" -> { state: { user: { access_token } } }.
//
// Read it the way utils/auth-fetch.ts does (from localStorage), NOT from the
// in-memory Zustand state: the host's refreshAccessToken() writes the new
// token straight to localStorage, so the in-memory store can lag behind it
// until the next reload. Going through the host's own getter keeps both
// clients on the same token at all times.
//
// There is no setter any more: login, refresh and logout are the host's.
import { getAccessToken } from "../../../../utils/auth-fetch";

export function getSessionToken(): string | undefined {
  const token: unknown = getAccessToken();
  return typeof token === "string" && token ? token : undefined;
}

// True when a session token is already available (e.g. rehydrated after a
// refresh) so the UI can show a signed-in state without forcing re-login.
export function hasSessionToken(): boolean {
  return Boolean(getSessionToken());
}

// Decodes a JWT's payload segment without verifying the signature. Shared by
// decodeJwtExpiry and decodeJwtDuties below so the base64url→atob→JSON.parse
// dance exists in one place.
function decodeJwtClaims(token: string): Record<string, unknown> | undefined {
  const payload = token.split(".")[1];
  if (!payload) return undefined;

  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(base64);
    const claims: unknown = JSON.parse(json);
    return claims && typeof claims === "object"
      ? (claims as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

// Decodes the `exp` claim (seconds since epoch) from a JWT's payload, without
// verifying the signature — used only to schedule a proactive refresh before
// the token the server already issued expires.
export function decodeJwtExpiry(token: string): number | undefined {
  const claims = decodeJwtClaims(token);
  const exp = claims?.exp;
  return typeof exp === "number" ? exp : undefined;
}

// Decodes the backend's duty list from the JWT's payload. It is nested under
// `ClaimsToken.duties`, NOT top-level like `exp`/`iat` — the gateway's login
// response shape, verified against a real token. Returns undefined (not an
// empty array) when the claim is missing entirely, so a caller can tell "no
// token / malformed token" apart from "a real user with zero duties".
export function decodeJwtDuties(token: string): readonly string[] | undefined {
  const claims = decodeJwtClaims(token);
  const claimsToken = claims?.ClaimsToken;
  if (!claimsToken || typeof claimsToken !== "object") return undefined;

  const duties = (claimsToken as Record<string, unknown>).duties;
  if (!Array.isArray(duties)) return undefined;

  return duties.filter((duty): duty is string => typeof duty === "string");
}

// Decodes the account's user-group ids from `ClaimsToken.groups` — an array of
// ObjectID strings (verified on a real staff token). Undefined when the token
// carries no ClaimsToken/groups, so "no claim" and "member of nothing" differ.
export function decodeJwtGroups(token: string): readonly string[] | undefined {
  const claims = decodeJwtClaims(token);
  const claimsToken = claims?.ClaimsToken;
  if (!claimsToken || typeof claimsToken !== "object") return undefined;

  const groups = (claimsToken as Record<string, unknown>).groups;
  if (!Array.isArray(groups)) return undefined;

  return groups.filter((group): group is string => typeof group === "string");
}

// Decodes the display name from the JWT's `ClaimsToken.name` claim — the
// account's real name, distinct from `.username` (the login handle). Returns
// undefined when the token carries no ClaimsToken/name at all.
export function decodeJwtName(token: string): string | undefined {
  const claims = decodeJwtClaims(token);
  const claimsToken = claims?.ClaimsToken;
  if (!claimsToken || typeof claimsToken !== "object") return undefined;

  const name = (claimsToken as Record<string, unknown>).name;
  return typeof name === "string" && name ? name : undefined;
}

const JWT_PATTERN = /^[\w-]+\.[\w-]+\.[\w-]+$/;

// Finds the JWT within a login response body without assuming the exact field
// name (token / access_token / accessToken / nested data.token, …). A JWT is
// three base64url segments separated by dots.
export function extractJwt(body: unknown, depth = 0): string | undefined {
  if (typeof body === "string") {
    return JWT_PATTERN.test(body) ? body : undefined;
  }

  if (depth > 3 || body === null || typeof body !== "object") {
    return undefined;
  }

  for (const value of Object.values(body as Record<string, unknown>)) {
    const found = extractJwt(value, depth + 1);
    if (found) {
      return found;
    }
  }

  return undefined;
}
